import { createContext, useContext, useState, useCallback, useMemo } from 'react';

const CartContext = createContext();

// Build a stable key based on item + sorted modifiers
function buildKey(menuItem, selectedOption, modifiers) {
  const optKey = selectedOption?.name_fr || 'default';
  const modKey = (modifiers || []).map(m => m.id).sort().join(',');
  return `${menuItem.id}-${optKey}-${modKey}`;
}

export function CartProvider({ children }) {
  const [items, setItems] = useState([]);

  const addItem = useCallback((menuItem, quantity = 1, selectedOption = null, modifiers = []) => {
    setItems(prev => {
      const key = buildKey(menuItem, selectedOption, modifiers);
      const existing = prev.find(i => i.key === key);
      if (existing) {
        return prev.map(i => i.key === key ? { ...i, quantity: i.quantity + quantity } : i);
      }
      return [...prev, { key, menuItem, quantity, selectedOption, modifiers }];
    });
  }, []);

  const removeItem = useCallback((key) => {
    setItems(prev => prev.filter(i => i.key !== key));
  }, []);

  const updateQuantity = useCallback((key, quantity) => {
    if (quantity <= 0) {
      setItems(prev => prev.filter(i => i.key !== key));
      return;
    }
    setItems(prev => prev.map(i => i.key === key ? { ...i, quantity } : i));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const cartData = useMemo(() => {
    const subtotal = items.reduce((sum, item) => {
      const basePrice = item.menuItem.price;
      const optionMod = item.selectedOption?.price_modifier || 0;
      const modsPrice = (item.modifiers || []).reduce((s, m) => s + (m.price || 0), 0);
      return sum + (basePrice + optionMod + modsPrice) * item.quantity;
    }, 0);
    const taxRate = 0.14975; // TPS 5% + TVQ 9.975%
    const taxes = subtotal * taxRate;
    const total = subtotal + taxes;
    const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
    return { items, subtotal, taxes, total, itemCount };
  }, [items]);

  return (
    <CartContext.Provider value={{ ...cartData, addItem, removeItem, updateQuantity, clearCart }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}
